import React from "react";
import { Box, Typography } from "@mui/material";
import { Send, CheckCircle, Layers, X } from "lucide-react";
import { Button } from "../../../components/Button";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { AlertNote } from "../../../components/AlertNote";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";

/**
 * Modal de confirmación unificado para acciones de contenido
 * Soporta tanto envío a cliente como aprobación de contenido
 * Incluye warnings especiales para grupos
 */
export function ContentConfirmationModal({
  open,
  onClose,
  onConfirm,
  action, // "send_to_client" | "approve" | "add_to_group" | "remove_from_group"
  contentData = {}, // { type, name, group_id, group_name, title, source_group_name, target_group_name }
  isLoading = false,
}) {
  const { neutrals } = useCustomTheme();

  const isSendToClient = action === "send_to_client";
  const isApprove = action === "approve";
  const isAddToGroup = action === "add_to_group";
  const isRemoveFromGroup = action === "remove_from_group";
  const isMoveBetweenGroups = Boolean(
    isAddToGroup && contentData?.source_group_name
  );
  
  // Determinar si es contenido de grupo
  const isGroupContent = contentData?.type === "group" || Boolean(contentData?.group_id);
  const groupName = contentData?.type === "group" 
    ? contentData?.name 
    : contentData?.group_name || "Grupo";
  
  // Configuración según acción
  const config = {
    send_to_client: {
      title: "Confirmar envío a cliente",
      icon: <Send size={16} />,
      confirmText: isSendToClient && isLoading ? "Enviando..." : "Confirmar envío",
      individualMessage: "¿Estás seguro de que deseas enviar este contenido a revisión del cliente?",
      individualSubMessage: "Se enviarán todas las adaptaciones de plataforma y el estado del contenido cambiará a \"Revisión\".",
      groupWarning: "Se enviarán a revisión todos los contenidos del grupo, no solo este. El estado del grupo y de todos sus contenidos cambiará a \"Revisión\".",
    },
    approve: {
      title: "Confirmar aceptación de contenido",
      icon: <CheckCircle size={16} />,
      confirmText: isApprove && isLoading ? "Aceptando..." : "Confirmar aceptación",
      individualMessage: "¿Estás seguro de que deseas marcar este contenido como aceptado?",
      groupWarning: "Se aceptarán todos los contenidos del grupo, no solo este. El estado del grupo y de todos sus contenidos cambiará a \"Aceptado\".",
    },
    add_to_group: {
      title: isMoveBetweenGroups
        ? "Confirmar cambio de grupo"
        : "Confirmar añadir al grupo",
      icon: <Layers size={16} />,
      confirmText: isLoading
        ? (isMoveBetweenGroups ? "Moviendo..." : "Añadiendo...")
        : (isMoveBetweenGroups ? "Mover al grupo" : "Añadir al grupo"),
    },
    remove_from_group: {
      title: "Confirmar quitar del grupo",
      icon: <X size={16} />,
      confirmText: isLoading ? "Quitando..." : "Quitar del grupo",
    },
  };

  if (!open) return null;

  const currentConfig = config[action];

  if (!currentConfig) {
    console.error(`ContentConfirmationModal: Acción no válida: ${action}`);
    return null;
  }

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={currentConfig.title}
      maxWidth="sm"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {(isAddToGroup || isRemoveFromGroup) ? (
          <>
            <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
              Contenido: <strong>"{contentData?.title || "Sin título"}"</strong>
            </Typography>
            {isAddToGroup && (
              <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
                {isMoveBetweenGroups ? (
                  <>
                    Este contenido se quitará del grupo{" "}
                    <strong>"{contentData?.source_group_name}"</strong> y se añadirá al grupo{" "}
                    <strong>"{contentData?.target_group_name}"</strong>.
                  </>
                ) : (
                  <>
                    Este contenido se añadirá al grupo{" "}
                    <strong>"{contentData?.target_group_name}"</strong>.
                  </>
                )}
              </Typography>
            )}
            {isRemoveFromGroup && (
              <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
                Este contenido se quitará del grupo{" "}
                <strong>"{contentData?.source_group_name || "Grupo"}</strong>.
              </Typography>
            )}
            <AlertNote variant="warning">
              La acción de quitar contenido de un grupo lo devuelve al estado "Borrador".
            </AlertNote>
          </>
        ) : isGroupContent ? (
          <>
            <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
              Este contenido pertenece al grupo <strong>"{groupName}"</strong>.
            </Typography>
            <AlertNote variant="warning">{currentConfig.groupWarning}</AlertNote>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
              {currentConfig.individualMessage}
            </Typography>
            <Typography variant="body2" sx={{ color: neutrals.gray[600], fontStyle: "italic" }}>
              {currentConfig.individualSubMessage}
            </Typography>
          </>
        )}

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
          >
            {currentConfig.icon}
            {currentConfig.confirmText}
          </Button>
        </Box>
      </Box>
    </UnifiedModal>
  );
}
